// src/utils/tabGuard.ts

/**
 * TabGuard v3 (correção de reload)
 *
 * Garante "uma aba por navegador" usando localStorage + heartbeat,
 * MAS agora o id da aba é persistido em sessionStorage para que
 * RELOAD/REFRESH na mesma aba não seja tratado como "outra aba".
 *
 * - Chave em localStorage: "eva_active_tab_v1"
 * - Estrutura: { id: string, lastSeen: number }
 * - Cada aba tem um id próprio (salvo em sessionStorage).
 * - A aba "dona" atualiza lastSeen a cada HEARTBEAT_MS.
 * - Se outra aba abrir e encontrar dono recente (não expirado), é BLOQUEADA.
 * - Se o dono sumir (fechou/crashou e TTL estourou), a próxima aba assume.
 */

const ACTIVE_KEY = "eva_active_tab_v1";
const TAB_ID_KEY = "eva_tab_id_v1";

// com 5s de heartbeat, 30s de TTL dá margem pra aba em background ser “throttled”
const HEARTBEAT_MS = 5000;
const STALE_MS = 30000;

type ActiveRecord = {
  id: string;
  lastSeen: number;
};

function now() {
  return Date.now();
}

function newId() {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `tab-${now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getOrCreateTabId(): string {
  // sessionStorage é por-aba e sobrevive a reload na MESMA aba
  try {
    if (
      typeof window !== "undefined" &&
      typeof sessionStorage !== "undefined"
    ) {
      const existing = sessionStorage.getItem(TAB_ID_KEY);
      if (existing && existing.trim()) return existing;

      const id = newId();
      sessionStorage.setItem(TAB_ID_KEY, id);
      return id;
    }
  } catch {
    // se sessionStorage falhar, cai no fallback (id novo por instância)
  }
  return newId();
}

function readActive(): ActiveRecord | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveRecord;
    if (
      !parsed ||
      typeof parsed.id !== "string" ||
      typeof parsed.lastSeen !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeActive(rec: ActiveRecord) {
  try {
    localStorage.setItem(ACTIVE_KEY, JSON.stringify(rec));
  } catch {
    // ignore
  }
}

function clearActiveIfOwner(id: string) {
  try {
    const current = readActive();
    if (current && current.id === id) {
      localStorage.removeItem(ACTIVE_KEY);
    }
  } catch {
    // ignore
  }
}

export class TabGuard {
  private id: string;
  private resolved = false;
  private allowed = false;
  private started = false;
  private heartbeatTimer: number | null = null;
  private unloadHandler?: () => void;

  constructor() {
    // ✅ ID persistente por ABA (não muda no reload)
    this.id = getOrCreateTabId();
  }

  /**
   * Inicializa o guard.
   * - callback(true): esta aba é a DONA e pode rodar o app.
   * - callback(false): já existe aba ativa → mostrar tela de "Aba já ativa".
   */
  init(callback: (allowed: boolean) => void) {
    // proteção contra StrictMode (effect rodando 2x)
    if (this.started) {
      callback(this.allowed);
      return;
    }
    this.started = true;

    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      // ambiente bizarro → não bloqueia nada
      this.allowed = true;
      this.resolved = true;
      callback(true);
      return;
    }

    try {
      const current = readActive();
      const t = now();

      if (!current) {
        // ninguém dono → esta aba assume
        this.becomeOwner();
        callback(true);
        return;
      }

      const isStale = t - current.lastSeen > STALE_MS;

      if (isStale) {
        // dono antigo expirou → esta aba assume
        this.becomeOwner();
        callback(true);
        return;
      }

      if (current.id === this.id) {
        // ✅ reload na mesma aba: continua dono
        this.becomeOwner();
        callback(true);
        return;
      }

      // Existe outro dono recente com id diferente → esta aba é BLOQUEADA
      this.allowed = false;
      this.resolved = true;
      callback(false);
    } catch {
      // se der algum erro inesperado, não bloqueia
      this.allowed = true;
      this.resolved = true;
      callback(true);
    }
  }

  private becomeOwner() {
    this.allowed = true;
    this.resolved = true;

    // grava como dono
    writeActive({ id: this.id, lastSeen: now() });

    // heartbeat pra manter a posse enquanto a aba está viva
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
    }
    this.heartbeatTimer = window.setInterval(() => {
      // só atualiza se ainda formos o dono
      const current = readActive();
      if (!current || current.id === this.id) {
        writeActive({ id: this.id, lastSeen: now() });
      }
    }, HEARTBEAT_MS) as unknown as number;

    // ao fechar, se ainda formos o dono, limpamos a chave
    this.unloadHandler = () => {
      clearActiveIfOwner(this.id);
    };

    window.addEventListener("beforeunload", this.unloadHandler);
    window.addEventListener("unload", this.unloadHandler);
  }

  /**
   * Libera recursos (limpa timers e listeners).
   * Não precisa manter nada se a aba for desmontada.
   */
  release() {
    this.resolved = true;

    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (this.unloadHandler) {
      window.removeEventListener("beforeunload", this.unloadHandler);
      window.removeEventListener("unload", this.unloadHandler);
      this.unloadHandler = undefined;
    }
  }
}
